import { useState } from 'react';
import type { CodeCitationAction, CodeCitation } from '../../types';
import styles from './CodeCitationCard.module.css';

/** Citations rendered in full before the rest collapse behind a toggle.
 *
 * An answer tracing an exception routinely cites five or six locations. Showing
 * them all expanded trades horizontal overflow for a vertical wall. */
const EXPANDED_BY_DEFAULT = 2;

/** Trailing path segments kept when shortening. */
const PATH_SEGMENTS = 3;

/** Shorten a repository path to its trailing segments.
 *
 * `src/main/java/com/cowbell/server/accountmodules/account/services/AccountService.java`
 * becomes `…/account/services/AccountService.java`. The filename is the part a
 * reader is actually looking for; the package prefix is noise once the repo is
 * already shown beside it. Full path stays in the title attribute.
 */
export function shortenPath(path: string, segments = PATH_SEGMENTS): string {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= segments) return path;
  return `…/${parts.slice(-segments).join('/')}`;
}

/** Short commit form, for showing what the chunk was pinned to. */
export function shortRef(ref: string): string {
  // Only abbreviate things that look like a SHA; a branch name must stay whole
  // or "main" would render as "mai".
  return /^[0-9a-f]{7,40}$/i.test(ref) ? ref.slice(0, 7) : ref;
}

function Citation({ citation }: { readonly citation: CodeCitation }) {
  const lines =
    citation.startLine && citation.endLine
      ? `L${citation.startLine}–${citation.endLine}`
      : '';

  return (
    <div className={styles.citation}>
      <div className={styles.header}>
        <span className={styles.repo}>{citation.repo}</span>
        <span className={styles.path} title={citation.path}>
          {shortenPath(citation.path)}
        </span>
        {lines && <span className={styles.lines}>{lines}</span>}
      </div>

      {citation.code && (
        <pre className={styles.code}>
          <code>{citation.code}</code>
        </pre>
      )}

      <div className={styles.footer}>
        {citation.ref && (
          <span
            className={`${styles.ref} ${
              citation.drifted ? styles.drifted : ''
            }`}
            title={
              citation.drifted
                ? 'Resolved against the default branch, not the deployed commit — line numbers may have moved.'
                : `Pinned to ${citation.ref}`
            }
          >
            {citation.drifted ? 'unpinned · ' : 'pinned · '}
            {shortRef(citation.ref)}
          </span>
        )}
        {citation.permalink && (
          <a
            className={styles.link}
            href={citation.permalink}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on GitHub ↗
          </a>
        )}
      </div>
    </div>
  );
}

interface Props {
  readonly action: CodeCitationAction;
}

export default function CodeCitationCard({ action }: Props) {
  const [expanded, setExpanded] = useState(false);
  const citations = action.citations ?? [];

  if (citations.length === 0) return null;

  const visible = expanded
    ? citations
    : citations.slice(0, EXPANDED_BY_DEFAULT);
  const hidden = citations.length - visible.length;

  return (
    <div className={styles.card}>
      {visible.map((citation, idx) => (
        <Citation
          key={`${citation.repo}-${citation.path}-${citation.startLine ?? idx}`}
          citation={citation}
        />
      ))}

      {hidden > 0 && (
        <button
          type="button"
          className={styles.more}
          onClick={() => setExpanded(true)}
        >
          Show {hidden} more location{hidden === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}
